import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material";

export interface FretboardMarker {
  stringNum: number; // 1 (high E) to 6 (low E)
  fretNum: number; // 1 to maxFret
  label?: string; // optional text inside the dot
  color?: string; // optional fill color for the dot (default: #1565c0)
  opacity?: number; // optional fill opacity, e.g. to ghost upcoming/planned notes (default: 1)
}

interface FretboardDiagramProps {
  markers?: FretboardMarker[];
  startFret?: number; // explicit start fret (default 1)
  numFretsToShow?: number; // default 5
  maxFret?: number; // highest fret the board extends to (default 12)
  highlightedStrings?: number[]; // string numbers (1-6) to draw in accent color
  onFretClick?: (stringNum: number, fretNum: number) => void;
  /** Text shown in a pill beside the tapped position for a moment after each tap. */
  tapLabel?: (stringNum: number, fretNum: number) => string;
}

interface Tap {
  id: number;
  x: number;
  y: number;
  label?: string;
  /** Open-string taps sit on the nut, so their pill hangs below instead of above. */
  labelBelow: boolean;
}

let tapIdCounter = 0;

// The ripple fades within this window; the pill lingers a little longer so the name can be read.
const TAP_LABEL_MS = 900;

const STRING_LABELS = ["E", "A", "D", "G", "B", "E"]; // low to high, left to right

const FretboardDiagram: React.FC<FretboardDiagramProps> = ({
  markers = [],
  startFret = 1,
  numFretsToShow = 5,
  maxFret = 12,
  highlightedStrings = [],
  onFretClick,
  tapLabel,
}) => {
  const [taps, setTaps] = useState<Tap[]>([]);
  const theme = useTheme();
  const endFret = Math.min(maxFret, startFret + numFretsToShow - 1);
  const fretsToShow = endFret - startFret + 1;
  const showNut = startFret === 1;
  // A window that starts mid-neck gets a "5fr"-style badge (the chord-chart
  // convention) so the starting position reads at a glance, not just from the
  // small fret numbers.
  const showStartFretBadge = startFret > 1;

  // Layout constants — sized to fill ~360px width (iPhone 14 minus page padding)
  const stringSpacing = 55;
  const fretSpacing = 92;
  const paddingLeft = showStartFretBadge ? 64 : 45;
  const paddingRight = 30;
  const paddingTop = 35;
  const paddingBottom = 25;
  const numStrings = 6;

  const fretboardWidth = (numStrings - 1) * stringSpacing;
  const fretboardHeight = fretsToShow * fretSpacing;
  const svgWidth = paddingLeft + fretboardWidth + paddingRight;
  const svgHeight = paddingTop + fretboardHeight + paddingBottom;

  // Wooden fretboard slab geometry — extends a little past the outer strings so
  // the strings sit on the wood rather than at its edge.
  const boardOverhang = stringSpacing * 0.3;
  const boardLeft = paddingLeft - boardOverhang;
  const boardRight = paddingLeft + fretboardWidth + boardOverhang;
  const boardWidth = boardRight - boardLeft;

  const getMarkerPosition = (stringNum: number, fretNum: number) => {
    const diagramCol = 6 - stringNum;
    const fretIndex = fretNum - startFret;
    return {
      x: paddingLeft + diagramCol * stringSpacing,
      y: paddingTop + fretIndex * fretSpacing + fretSpacing / 2,
    };
  };

  const handleFretClick = useCallback(
    (stringNum: number, fretNum: number) => {
      if (!onFretClick) return;
      onFretClick(stringNum, fretNum);
      const pos =
        fretNum === 0
          ? {
              x: paddingLeft + (6 - stringNum) * stringSpacing,
              y: paddingTop,
            }
          : getMarkerPosition(stringNum, fretNum);
      const id = ++tapIdCounter;
      const label = tapLabel?.(stringNum, fretNum);
      setTaps((prev) => [...prev, { id, x: pos.x, y: pos.y, label, labelBelow: fretNum === 0 }]);
      setTimeout(() => {
        setTaps((prev) => prev.filter((t) => t.id !== id));
      }, TAP_LABEL_MS);
    },
    [onFretClick, tapLabel, startFret]
  );

  // Flat, size-independent colors so the fretboard material renders identically
  // at every board size (5-fret questions, short windows near the 12th fret, and
  // the tall 15-fret Explore board) and across every mounted instance.
  const WOOD_FILL = "#4a3526";
  const WOOD_BORDER = "#2a1c11";
  const NUT_FILL = "#e9ddc4";
  const NUT_BORDER = "#8a7547";
  const FRET_FILL = "#d4d8dc";
  const INLAY_FILL = "#efe7d4";
  const STRING_FILL = "#bda678";
  // Dark edge drawn just under each string so it stays a continuous, defined
  // line where it crosses the bright frets (otherwise the low string/fret
  // contrast breaks each string into segments that look misaligned).
  const STRING_CASING = "#1f1509";
  const ACCENT = "#1565c0";
  const BADGE_FILL = "#c0883c"; // brass — reads on both the light and dark page
  const BADGE_TEXT = "#2a1c11";
  // The tap pill floats over the wood, so it borrows the nut's bone-on-dark contrast.
  const TAP_LABEL_FILL = "#e9ddc4";
  const TAP_LABEL_TEXT = "#2a1c11";
  // Text outside the board sits on the page background, so it follows the theme.
  const LABEL_FILL = theme.palette.text.secondary;

  const markerSummary =
    markers.length > 0
      ? `, with ${markers.length} marked ${markers.length === 1 ? "position" : "positions"}`
      : "";
  const ariaLabel = `Guitar fretboard diagram, frets ${startFret} to ${endFret}${markerSummary}`;

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      style={{ maxWidth: "100%" }}
      role="img"
      aria-label={ariaLabel}
    >
      {/* Wooden fretboard slab */}
      <rect
        x={boardLeft}
        y={paddingTop}
        width={boardWidth}
        height={fretboardHeight}
        rx={8}
        fill={WOOD_FILL}
        stroke={WOOD_BORDER}
        strokeWidth={1.5}
      />

      {/* String labels at top */}
      {STRING_LABELS.map((label, i) => (
        <text
          key={`label-${i}`}
          x={paddingLeft + i * stringSpacing}
          y={paddingTop - 14}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fontFamily="sans-serif"
          fill={LABEL_FILL}
        >
          {label}
        </text>
      ))}

      {/* Nut or top fret line */}
      {showNut ? (
        <rect
          x={boardLeft}
          y={paddingTop - 7}
          width={boardWidth}
          height={14}
          fill={NUT_FILL}
          stroke={NUT_BORDER}
          strokeWidth={1.5}
          rx={2.5}
        />
      ) : (
        <rect
          x={boardLeft}
          y={paddingTop - 2}
          width={boardWidth}
          height={4}
          fill={FRET_FILL}
          rx={2}
        />
      )}

      {/* Fret wires */}
      {Array.from({ length: fretsToShow }, (_, i) => {
        const y = paddingTop + (i + 1) * fretSpacing;
        return (
          <rect
            key={`fret-${i}`}
            x={boardLeft}
            y={y - 2}
            width={boardWidth}
            height={4}
            fill={FRET_FILL}
            rx={2}
          />
        );
      })}

      {/* Fret numbers on the left; the first row's number is replaced by the
          start-fret badge when the window starts mid-neck. */}
      {Array.from({ length: fretsToShow }, (_, i) => {
        if (showStartFretBadge && i === 0) return null;
        const y = paddingTop + i * fretSpacing + fretSpacing / 2;
        return (
          <text
            key={`fretnum-${i}`}
            x={boardLeft - 13}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="14"
            fontWeight="600"
            fontFamily="sans-serif"
            fill={LABEL_FILL}
          >
            {startFret + i}
          </text>
        );
      })}

      {/* Start-fret badge ("5 fr"), aligned with the window's first fret */}
      {showStartFretBadge && (
        <g>
          <rect
            x={4}
            y={paddingTop + fretSpacing / 2 - 12}
            width={46}
            height={24}
            rx={12}
            fill={BADGE_FILL}
          />
          <text
            x={27}
            y={paddingTop + fretSpacing / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="13"
            fontWeight="800"
            fontFamily="sans-serif"
            fill={BADGE_TEXT}
          >
            {startFret} fr
          </text>
        </g>
      )}

      {/* Fret inlay dots */}
      {Array.from({ length: fretsToShow }, (_, i) => {
        const fret = startFret + i;
        const centerY = paddingTop + i * fretSpacing + fretSpacing / 2;
        const centerX = paddingLeft + fretboardWidth / 2;
        const dotRadius = 6;
        const isSingleDot = [3, 5, 7, 9, 15, 17, 19, 21].includes(fret);
        const isDoubleDot = fret === 12 || fret === 24;

        if (isSingleDot) {
          return (
            <circle key={`inlay-${i}`} cx={centerX} cy={centerY} r={dotRadius} fill={INLAY_FILL} />
          );
        }
        if (isDoubleDot) {
          const offset = stringSpacing;
          return (
            <React.Fragment key={`inlay-${i}`}>
              <circle cx={centerX - offset} cy={centerY} r={dotRadius} fill={INLAY_FILL} />
              <circle cx={centerX + offset} cy={centerY} r={dotRadius} fill={INLAY_FILL} />
            </React.Fragment>
          );
        }
        return null;
      })}

      {/* Strings (vertical lines) */}
      {Array.from({ length: numStrings }, (_, i) => {
        const x = paddingLeft + i * stringSpacing;
        // Column i (left to right) corresponds to string number 6-i (low E=6 on left, high E=1 on right)
        const appStringNum = 6 - i;
        const isHighlighted = highlightedStrings.includes(appStringNum);
        // Strings taper slightly from the thick wound low E (col 0) to the high E,
        // but stay thick enough that even the thinnest reads as the same color.
        const naturalWidth = 2.8 - i * 0.16;
        const width = isHighlighted ? Math.max(5, naturalWidth) : naturalWidth;
        const y1 = paddingTop;
        const y2 = paddingTop + fretboardHeight;
        return (
          <React.Fragment key={`string-${i}`}>
            <line
              x1={x}
              y1={y1}
              x2={x}
              y2={y2}
              stroke={STRING_CASING}
              strokeWidth={width + 4.5}
              strokeLinecap="round"
            />
            <line
              x1={x}
              y1={y1}
              x2={x}
              y2={y2}
              stroke={isHighlighted ? ACCENT : STRING_FILL}
              strokeWidth={width}
              strokeLinecap="round"
            />
          </React.Fragment>
        );
      })}

      {/* Markers */}
      {markers.map((marker, i) => {
        const { x, y } = getMarkerPosition(marker.stringNum, marker.fretNum);
        return (
          <g key={`marker-${i}`} opacity={marker.opacity ?? 1}>
            <circle
              cx={x}
              cy={y}
              r={16}
              fill={marker.color || ACCENT}
              stroke="#ffffff"
              strokeWidth={1.75}
            />
            {/* Subtle top highlight for a glossy, raised look */}
            <ellipse cx={x} cy={y - 5} rx={9} ry={5} fill="#ffffff" opacity={0.18} />
            {marker.label && (
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="11"
                fontWeight="700"
                fill="white"
              >
                {marker.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Clickable open string regions (string labels) */}
      {onFretClick &&
        Array.from({ length: numStrings }, (_, colIdx) => {
          const stringNum = 6 - colIdx;
          const x = paddingLeft + colIdx * stringSpacing - stringSpacing / 2;
          return (
            <rect
              key={`click-${stringNum}-0`}
              x={x}
              y={0}
              width={stringSpacing}
              height={paddingTop}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onClick={() => handleFretClick(stringNum, 0)}
            />
          );
        })}

      {/* Clickable fret regions */}
      {onFretClick &&
        Array.from({ length: fretsToShow }, (_, fretIdx) => {
          const fretNum = startFret + fretIdx;
          return Array.from({ length: numStrings }, (_, colIdx) => {
            const stringNum = 6 - colIdx;
            const x = paddingLeft + colIdx * stringSpacing - stringSpacing / 2;
            const y = paddingTop + fretIdx * fretSpacing;
            return (
              <rect
                key={`click-${stringNum}-${fretNum}`}
                x={x}
                y={y}
                width={stringSpacing}
                height={fretSpacing}
                fill="transparent"
                style={{ cursor: "pointer" }}
                onClick={() => handleFretClick(stringNum, fretNum)}
              />
            );
          });
        })}

      {/* Tap feedback: a ripple at the position, plus the note-name pill when one was given */}
      {taps.map((tap) => (
        <g key={tap.id} pointerEvents="none">
          <circle
            cx={tap.x}
            cy={tap.y}
            r={16}
            fill="rgba(21, 101, 192, 0.4)"
            style={{ animation: "fretboard-ripple 400ms ease-out forwards" }}
          />
          {tap.label && (
            <g
              transform={`translate(${tap.x}, ${tap.y + (tap.labelBelow ? 30 : -30)})`}
              style={{ animation: `fretboard-tap-label ${TAP_LABEL_MS}ms ease-out forwards` }}
            >
              <rect
                x={-(tap.label.length * 8 + 16) / 2}
                y={-11}
                width={tap.label.length * 8 + 16}
                height={22}
                rx={11}
                fill={TAP_LABEL_FILL}
                stroke={NUT_BORDER}
                strokeWidth={1}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="12"
                fontWeight="700"
                fontFamily="sans-serif"
                fill={TAP_LABEL_TEXT}
              >
                {tap.label}
              </text>
            </g>
          )}
        </g>
      ))}

      {/* Tap feedback keyframes */}
      <defs>
        <style>{`
          @keyframes fretboard-ripple {
            0% { r: 8; opacity: 0.5; }
            100% { r: 24; opacity: 0; }
          }
          @keyframes fretboard-tap-label {
            0%, 65% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
      </defs>
    </svg>
  );
};

export default FretboardDiagram;
